import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

interface CacheRemoverOptions {
  method: string;
  headers: {
    'Content-Type': string;
    'X-Auth-Key': string;
    'X-Auth-Email': string;
  };
  body: string;
}

interface FetchOptions extends RequestInit {
  headers: HeadersInit;
}

interface ErrorResponse {
  errors: Array<{ message: string }>;
}

type QueryParams = Record<string, string | number | boolean | undefined | null>;

interface GetResultOptions extends Omit<AxiosRequestConfig, 'params'> {
  params?: QueryParams;
}

class Fetchdata {
  private readonly requestTimeoutMs = 20000;

  private buildUrlWithParams(apiurl: string, params?: QueryParams): string {
    const trimmedUrl = apiurl.trim();
    if (!params) {
      return trimmedUrl;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    if (!queryString) {
      return trimmedUrl;
    }

    const [baseUrl, existingQuery = ''] = trimmedUrl.split('?');
    const mergedParams = new URLSearchParams(existingQuery);
    searchParams.forEach((value, key) => mergedParams.set(key, value));

    return `${baseUrl}?${mergedParams.toString()}`;
  }

  /** Relative URLs are proxied in development; auth headers are added by the proxy. */
  private shouldAttachAuthHeaders(apiurl: string): boolean {
    return !apiurl.startsWith('/');
  }

  /** Required auth headers for all Bharat API GET requests */
  private getAuthHeaders(): Record<string, string> {
    const apiKey = process.env.REACT_APP_X_API_KEY;
    const apiSiteUrl = process.env.REACT_APP_API_SITE_URL;
    

    if (!apiKey?.trim()) {
      throw new Error('Missing API_KEY. API requests cannot proceed without it.');
    }
    if (!apiSiteUrl?.trim()) {
      throw new Error('Missing SITE_KEY. API requests cannot proceed without it.');
    }

    return {
      'X-API-KEY': "Apikey103&3406##",
      'X-REQUEST-ORIGIN': "https://m.bharat24live.com",
    };
  }



  private handleAxiosError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      console.error('Axios Error:', error.message);
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request Timeout: The server took too long to respond.');
      }
      if (error.response) {
        console.error('Error Status:', error.response.status);
        console.error('Error Data:', error.response.data);
        const message =
          (error.response.data as { message?: string })?.message || 'Unknown error';
        throw new Error(`API Error: ${error.response.status} - ${message}`);
      }
      if (error.request) {
        console.error('No response received:', error.request);
        throw new Error('Network Error: No response from server.');
      }
      console.error('Request setup error:', error.message);
      throw new Error(`Request Error: ${error.message}`);
    }
    console.error('Unexpected Error:', error);
    throw new Error(`An unexpected error occurred: ${(error as Error).message}`);
  }

  async getWithArgs(url: string, params: Record<string, any>): Promise<any> {
    if (!url?.trim()) {
      throw new Error('getWithArgs: URL is required.');
    }
    try {
      const config: AxiosRequestConfig = {
        params,
        headers: this.getAuthHeaders(),
        timeout: this.requestTimeoutMs,
      };
      const response: AxiosResponse = await axios.get(url, config);
      return response.data;
    } catch (error) {
      this.handleAxiosError(error);
    }
  }

  async getResult(apiurl: string, options?: GetResultOptions): Promise<any> {
    if (!apiurl?.trim()) {
      throw new Error('getResult: API URL is required.');
    }

    const { params, ...axiosConfig } = options ?? {};
    const url = this.buildUrlWithParams(apiurl, params);
    const authHeaders = this.shouldAttachAuthHeaders(url) ? this.getAuthHeaders() : {};

    try {
      const response = await axios.get(url, {
        ...axiosConfig,
        headers: {
          ...authHeaders,
          ...(axiosConfig.headers as Record<string, string> | undefined),
        },
        timeout: axiosConfig.timeout ?? this.requestTimeoutMs,
      });
      return response.data;
    } catch (error) {
      this.handleAxiosError(error);
    }
  }




  async cacheRemover(url: string): Promise<any> {
    try {
      const options: CacheRemoverOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Key': '4050f9b90bd8642618b538d541d2bb3f67863',
          'X-Auth-Email': 'info@kodecube.com',
        },
        body: JSON.stringify({ purge_everything: true }),
      };

      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json();
        throw new Error(`Error: ${errorData.errors[0].message}`);
      }

      const data = await response.json();
      console.log('Request succeeded with JSON response:', data);
      return data;
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
      throw error;
    }
  }

  async postRequest(
    apiurl: string,
    postData: any,
    contentType: string = 'application/json'
  ): Promise<any> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': contentType,
      };

      const body: string =
        contentType === 'application/json' ? JSON.stringify(postData) : postData;

      const options: FetchOptions = {
        method: 'POST',
        headers,
        body,
      };

      const response = await fetch(apiurl, options);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('There was an error with the fetch operation:', error.message);
      throw error;
    }
  }

  async deleteRequest(apiUrl: string, data: any): Promise<any> {
    try {
      const options: FetchOptions = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(apiUrl, options);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error in deleteRequest:', error);
      throw error;
    }
  }

  async patchRequest(apiUrl: string, data: any): Promise<any> {
    try {
      const options: FetchOptions = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(apiUrl, options);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error in patchRequest:', error);
      throw error;
    }
  }
}

const fetchData = new Fetchdata();
export default fetchData;